import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, skip, Subject, takeUntil } from 'rxjs';
import { ICartLineInput, IShopifyCart, IShopifyProduct, IShopifyProductVariant, SEO } from '@app/_shared/_models';
import { CartService } from '../../_shared/_services/cart.service';
import { GoogleAnalyticsService } from '../../_shared/_services/google-analytics.service';
import { ProductService } from '../../_shared/_services/product.service';
import { SeoService } from '../../_shared/_services/seo.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { SubscriptionComponent } from '../../_shared/_components/subscription/subscription.component';
import { MatIcon } from '@angular/material/icon';
import { SimilarProductsComponent } from '../../_shared/_components/similar-products/similar-products.component';

@Component({
    selector: 'app-variant',
    templateUrl: './variant.component.html',
    styleUrls: ['./variant.component.scss'],
    imports: [NgxSkeletonLoaderComponent, NgClass, MatButton, SubscriptionComponent, MatIcon, SimilarProductsComponent, AsyncPipe]
})
export class VariantComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly productService = inject(ProductService);
  private readonly cartService = inject(CartService);
  private readonly titleService = inject(Title);
  private readonly seoService = inject(SeoService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  public seo: SEO = {
    image: '',
    metaDescription: '',
    metaTitle: ''
  };
  public variantIndex = 0;
  public handle!: string | null;
  public product$!: Observable<IShopifyProduct | undefined>;
  public amountToAddToCart = 1;
  public isDescriptionExpanded = false;
  public inStockDescription = 'In Stock';
  public amountInCart = 0;

  private product: IShopifyProduct | undefined;
  private cart: IShopifyCart | undefined;
  private variantId = '';
  private selectedSubscription = '';
  private readonly unsubscribe: Subject<undefined> = new Subject<undefined>();

  constructor() {
    this.product$ = this.productService.product$;
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (params: any) => {
        const handle = params['name'];
        const variantId = params['variantId'];
        this.variantId = variantId;
        if (handle) {
          this.handle = handle;
          this.seoService.createCanonicalUrl(`https://mypoolhut.com/products/${handle}`);
          if (this.handle) {
            await this.productService.loadProductByHandle(this.handle);
          }
        }
    });

    this.product$
      .pipe(skip(1), takeUntil(this.unsubscribe))
      .subscribe(async (product: IShopifyProduct | undefined) => {
        if (product) {
          this.product = product;
          const index = product.variants.findIndex((v: IShopifyProductVariant) => v.id.includes(this.variantId));

          if (this.variantId && index > -1) {
            await this.selectVariant(product, index);
            this.calculateCartAmount();
          } else {
            if (this.router.url?.includes('/products/')) {
              await this.router.navigate(['/products/product-not-found']);
            }
          }
        } else {
          const regex = /^\/products\/[^/]+\/.+$/;
          if (this.router.url?.includes('/products/') && regex.test(this.router.url)) {
            await this.router.navigate(['/products/product-not-found']);
          }
        }
      });

    this.cartService.cart$
      .pipe(
        takeUntil(this.unsubscribe)
      )
      .subscribe((cart: IShopifyCart | undefined) => {
        if (cart) {
          this.cart = cart;
          this.calculateCartAmount();
        }
      });
  }

  ngOnDestroy(): void {
    this.seoService.removeCanonicalUrl();
    this.productService.unloadProduct();
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  public async selectVariant(product: IShopifyProduct, index: number): Promise<void> {
    this.selectedSubscription = '';
    const urlParts = product.variants[index]?.id?.split('/');
    const variantId = urlParts[urlParts.length - 1];
    if (this.variantId !== variantId) {
      this.productService.unloadProduct();
      await this.router.navigate([`/products/${this.handle}`, variantId]);
    }
    this.variantIndex = index;
    this.changeDetectorRef.detectChanges();
    this.seo.image = product.variants[index]?.image?.url;
    const metaDescription = product.descriptionHtml.substring(
      product.descriptionHtml.indexOf('<p>') + 3,
      product.descriptionHtml.indexOf('</p>')
    );
    this.seo.metaDescription = metaDescription.replace(/<\/?[^>]+(>|$)/g, '');

    if (product.seo.description) {
      this.seo.metaDescription = product.seo.description;
    }
    this.seo.metaTitle = `${product.title} - ${product.variants[index]?.variantTitle}`;
    this.seoService.setMetaData(this.seo);
    this.titleService.setTitle(`${product.title} - ${product.variants[index]?.variantTitle} | My Pool Hut`);
  }

  public add(variant: IShopifyProductVariant): void {
    if (variant.inventory > this.amountToAddToCart) {
      this.amountToAddToCart += 1;
    }
  }

  public remove(): void {
    if (this.amountToAddToCart > 1) {
      this.amountToAddToCart -= 1;
    }
  }

  public addProductToCart(variant: IShopifyProductVariant): void {
    this.cartService.insertCartItem(variant, this.amountToAddToCart, this.selectedSubscription);
    this.cartService.openDialog();
    this.googleAnalyticsService.itemAddedToCartConversion(variant?.price);
    this.googleAnalyticsService.eventEmitter('Added Product to Cart', 'Shopping Cart', 'Added Product to Cart', 'Shopping Cart', 1);
  }

  public selectedSubscriptionEvent(event: any): void {
    this.selectedSubscription = event;
  }

  private calculateCartAmount(): void {
    this.amountInCart = 0;
    const cartLines = this.cart?.lines?.filter(item => item.variant.id === this.product?.variants[this.variantIndex]?.id);

    if (cartLines && cartLines.length > 0) {
      cartLines.forEach((line: ICartLineInput) => {
        this.amountInCart += line.quantity;
      });
    }
  }
}
